/* eslint-disable react/prop-types */
import React from 'react';
import { Button, ModalBody, ModalFooter, ModalHeader } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCreditCard,
  faExclamationTriangle
} from '@fortawesome/free-solid-svg-icons';
import api from 'util/api';
import logger from 'util/logger';
import { navigateTo } from 'util/navigation';
import SoftBadge from 'components/common/SoftBadge';

const CreditCardNotificationDialog = ({ close, modalProps }) => {
  // Extract data from modalProps
  const notificationData = modalProps?.notifications;
  const primaryCard = modalProps?.primaryCard;
  const backupCard = modalProps?.backupCard;

  const closeModal = res => {
    close(res);
  };

  const handleUpdateCards = () => {
    // Close the modal and redirect to payment method page
    closeModal(notificationData?.Id || '');
    navigateTo('/billing/payment-method');
  };

  const handleRemindMeLater = async () => {
    try {
      const NotificationType = notificationData?.Id;
      const notificationPayload = {
        NotificationType: NotificationType,
        Action: 'remind_later'
      };
      const referenceguid =
        NotificationType === 'expiring_card_alert_primary'
          ? primaryCard?.GUID
          : NotificationType === 'expiring_card_alert_backup'
          ? backupCard?.GUID
          : null;

      //if referenceguid is not null, add it to the notification payload
      if (referenceguid) {
        notificationPayload.ReferenceId = referenceguid;
      }

      if (NotificationType) {
        await api.creditcardsNotifications.action(notificationPayload);
      }
    } catch (error) {
      logger.log('Error calling notificationAction API:', error);
    } finally {
      closeModal('remind_later');
    }
  };

  const handleDontRemindMeAgain = async () => {
    try {
      const NotificationType = notificationData?.Id;
      const notificationPayload = {
        NotificationType: NotificationType,
        Action: 'dont_remind'
      };
      const referenceguid =
        NotificationType === 'expiring_card_alert_primary'
          ? primaryCard?.GUID
          : NotificationType === 'expiring_card_alert_backup'
          ? backupCard?.GUID
          : null;

      //if referenceguid is not null, add it to the notification payload
      if (referenceguid) {
        notificationPayload.ReferenceId = referenceguid;
      }
      if (NotificationType) {
        await api.creditcardsNotifications.action(notificationPayload);
      }
    } catch (error) {
      logger.log('Error calling notificationAction API:', error);
    } finally {
      closeModal('dont_remind');
    }
  };

  return (
    <>
      <ModalHeader toggle={() => closeModal(null)} className="pb-2">
        <h5 className="mb-1 fw-bold text-dark">{notificationData?.Title}</h5>
      </ModalHeader>

      <ModalBody>
        <div>
          <div className="alert alert-danger mb-3">
            <div className="d-flex align-items-start">
              <FontAwesomeIcon
                icon={faExclamationTriangle}
                className="me-2 mt-1"
              />
              <p className="mb-0">{notificationData?.Message}</p>
            </div>
          </div>

          {primaryCard && (
            <div className="bg-light rounded p-3 mb-2">
              <div className="d-flex align-items-center mb-2">
                <FontAwesomeIcon
                  icon={faCreditCard}
                  className="text-primary me-2"
                />
                <strong>Current Status:</strong>
              </div>
              <div className="ms-4">
                <div className="d-flex align-items-center mb-1">
                  <SoftBadge pill bg="success" className="me-2" size="sm">
                    Primary
                  </SoftBadge>
                  <span className="text-muted">
                    •••• •••• •••• {primaryCard.CardLast4} (
                    {primaryCard.CardType})
                  </span>
                </div>
                {backupCard && (
                  <div className="d-flex align-items-center mb-1">
                    <SoftBadge pill bg="warning" className="me-2" size="sm">
                      Backup
                    </SoftBadge>
                    <span className="text-muted">
                      •••• •••• •••• {backupCard.CardLast4} (
                      {backupCard.CardType})
                    </span>
                  </div>
                )}
              </div>
            </div>
          )}

          <div className="alert alert-info mb-0">
            <strong>💡 Discount Protection:</strong> Having both a Primary and
            Backup card ensures you never lose your valuable discount.
          </div>
        </div>
      </ModalBody>

      <ModalFooter
        className="border-top-0 pt-0"
        style={{ padding: '1rem 2rem 2rem' }}
      >
        {notificationData?.Snoozeable && (
          <>
            <Button
              variant="outline-secondary"
              onClick={handleDontRemindMeAgain}
              className="me-2"
            >
              Don&apos;t Remind Me
            </Button>
            <Button
              variant="dark"
              onClick={handleRemindMeLater}
              className="me-2"
            >
              Remind Me Later
            </Button>
          </>
        )}
        <Button
          variant="primary"
          onClick={handleUpdateCards}
          className="d-flex align-items-center"
        >
          <FontAwesomeIcon icon={faCreditCard} className="me-2" />
          Update Cards
        </Button>
      </ModalFooter>
    </>
  );
};

export default CreditCardNotificationDialog;
